import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Droplets, Type, Image as ImageIcon } from "lucide-react";
import { PDFDocument, rgb, degrees, StandardFonts } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function WatermarkPdf() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [watermarkType, setWatermarkType] = useState("text");
  const [watermarkText, setWatermarkText] = useState("CONFIDENTIAL");
  const [watermarkImage, setWatermarkImage] = useState(null);
  const [fontSize, setFontSize] = useState([48]);
  const [opacity, setOpacity] = useState([30]);
  const [rotation, setRotation] = useState([-45]);
  const [textColor, setTextColor] = useState("#ff0000");
  const [position, setPosition] = useState("center");
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Watermark PDF - Add Watermark to PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      await loadPdfPreview(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      await loadPdfPreview(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdfPreview = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setTotalPages(pdf.numPages);
      
      // Get first page preview
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({ canvasContext: context, viewport }).promise;
      setPreviewImage(canvas.toDataURL());
      
      toast.success(`PDF loaded: ${pdf.numPages} pages`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
    setIsProcessing(false);
  };

  const handleImageUpload = (e) => {
    const imageFile = e.target.files?.[0];
    if (imageFile && imageFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setWatermarkImage(event.target.result);
        toast.success("Watermark image loaded");
      };
      reader.readAsDataURL(imageFile);
    } else {
      toast.error("Please select a valid image file");
    }
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16) / 255,
      g: parseInt(result[2], 16) / 255,
      b: parseInt(result[3], 16) / 255
    } : { r: 1, g: 0, b: 0 };
  };

  const addWatermark = async () => {
    if (!pdfBytes) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (watermarkType === "text" && !watermarkText.trim()) {
      toast.error("Please enter watermark text");
      return;
    }

    if (watermarkType === "image" && !watermarkImage) {
      toast.error("Please upload a watermark image");
      return;
    }

    setIsProcessing(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const pages = pdfDoc.getPages();
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const color = hexToRgb(textColor);

      for (const page of pages) {
        const { width, height } = page.getSize();

        if (watermarkType === "text") {
          const textWidth = font.widthOfTextAtSize(watermarkText, fontSize[0]);
          const textHeight = font.heightAtSize(fontSize[0]);

          let x, y;
          switch (position) {
            case "top-left":
              x = 50;
              y = height - 50;
              break;
            case "top-right":
              x = width - textWidth - 50;
              y = height - 50;
              break;
            case "bottom-left":
              x = 50;
              y = 50;
              break;
            case "bottom-right":
              x = width - textWidth - 50;
              y = 50;
              break;
            case "center":
            default:
              x = (width - textWidth) / 2;
              y = height / 2;
              break;
          }

          page.drawText(watermarkText, {
            x,
            y,
            size: fontSize[0],
            font,
            color: rgb(color.r, color.g, color.b),
            opacity: opacity[0] / 100,
            rotate: degrees(rotation[0])
          });
        } else if (watermarkType === "image" && watermarkImage) {
          // Embed image
          let embeddedImage;
          if (watermarkImage.includes('data:image/png')) {
            const base64Data = watermarkImage.split(',')[1];
            const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
            embeddedImage = await pdfDoc.embedPng(imageBytes);
          } else {
            const base64Data = watermarkImage.split(',')[1];
            const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
            embeddedImage = await pdfDoc.embedJpg(imageBytes);
          }

          const imgWidth = 150;
          const imgHeight = (embeddedImage.height / embeddedImage.width) * imgWidth;

          let x, y;
          switch (position) {
            case "top-left":
              x = 50;
              y = height - imgHeight - 50;
              break;
            case "top-right":
              x = width - imgWidth - 50;
              y = height - imgHeight - 50;
              break;
            case "bottom-left":
              x = 50;
              y = 50;
              break;
            case "bottom-right":
              x = width - imgWidth - 50;
              y = 50;
              break;
            case "center":
            default:
              x = (width - imgWidth) / 2;
              y = (height - imgHeight) / 2;
              break;
          }

          page.drawImage(embeddedImage, {
            x,
            y,
            width: imgWidth,
            height: imgHeight,
            opacity: opacity[0] / 100
          });
        }
      }

      const watermarkedPdfBytes = await pdfDoc.save();
      const blob = new Blob([watermarkedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_watermarked.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Watermarked PDF downloaded!");
    } catch (error) {
      console.error("Error adding watermark:", error);
      toast.error("Failed to add watermark: " + error.message);
    }
    setIsProcessing(false);
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setPreviewImage(null);
    setTotalPages(0);
  };

  const positions = [
    { value: "center", label: "Center" },
    { value: "top-left", label: "Top Left" },
    { value: "top-right", label: "Top Right" },
    { value: "bottom-left", label: "Bottom Left" },
    { value: "bottom-right", label: "Bottom Right" }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-cyan-50 to-blue-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Droplets className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full mb-4">
            <Droplets className="w-5 h-5" />
            <span className="font-medium">Watermark PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Add Watermark to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Add text or image watermarks to protect your PDF documents.
          </p>
        </div>

        {/* Upload Area */}
        {!file && (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-cyan-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF to add watermark
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {file && (
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Preview */}
              <Card className="dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-cyan-500" />
                      <h3 className="font-semibold dark:text-white">{file.name}</h3>
                    </div>
                    <Badge variant="outline">{totalPages} pages</Badge>
                  </div>
                  
                  {previewImage ? (
                    <div className="relative bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                      <img
                        src={previewImage}
                        alt="PDF Preview"
                        className="w-full h-auto"
                      />
                      {/* Watermark preview overlay */}
                      {watermarkType === "text" && watermarkText && (
                        <div 
                          className="absolute inset-0 flex items-center justify-center pointer-events-none"
                          style={{
                            transform: `rotate(${rotation[0]}deg)`,
                            opacity: opacity[0] / 100
                          }}
                        >
                          <span 
                            style={{ 
                              fontSize: `${fontSize[0] * 0.5}px`,
                              color: textColor,
                              fontWeight: 'bold'
                            }}
                          >
                            {watermarkText}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
                    </div>
                  )}
                  
                  <Button variant="outline" size="sm" onClick={clearFile} className="mt-4">
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </CardContent>
              </Card>

              {/* Watermark Settings */}
              <div className="space-y-4">
                <Card className="dark:bg-slate-800">
                  <CardContent className="p-4 space-y-4">
                    <h3 className="font-semibold dark:text-white">Watermark Type</h3>
                    <div className="flex gap-2">
                      <Button
                        variant={watermarkType === "text" ? "default" : "outline"}
                        onClick={() => setWatermarkType("text")}
                        className={watermarkType === "text" ? "bg-cyan-600" : ""}
                        size="sm"
                      >
                        <Type className="w-4 h-4 mr-1" /> Text
                      </Button>
                      <Button
                        variant={watermarkType === "image" ? "default" : "outline"}
                        onClick={() => setWatermarkType("image")}
                        className={watermarkType === "image" ? "bg-cyan-600" : ""}
                        size="sm"
                      >
                        <ImageIcon className="w-4 h-4 mr-1" /> Image
                      </Button>
                    </div>

                    {watermarkType === "text" ? (
                      <>
                        <div>
                          <Label className="text-sm">Watermark Text</Label>
                          <Input
                            value={watermarkText}
                            onChange={(e) => setWatermarkText(e.target.value)}
                            placeholder="Enter watermark text..."
                            className="mt-1"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm">Font Size: {fontSize[0]}px</Label>
                            <Slider
                              value={fontSize}
                              onValueChange={setFontSize}
                              min={12}
                              max={120}
                              step={2}
                              className="mt-2"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Color</Label>
                            <Input
                              type="color"
                              value={textColor}
                              onChange={(e) => setTextColor(e.target.value)}
                              className="mt-1 h-9"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <Label className="text-sm">Upload Watermark Image</Label>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => imageInputRef.current?.click()}
                          className="w-full mt-1"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {watermarkImage ? "Change Image" : "Select Image"}
                        </Button>
                        {watermarkImage && (
                          <img src={watermarkImage} alt="Watermark" className="mt-2 h-16 object-contain" />
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-sm">Opacity: {opacity[0]}%</Label>
                      <Slider
                        value={opacity}
                        onValueChange={setOpacity}
                        min={5}
                        max={100}
                        step={5}
                        className="mt-2"
                      />
                    </div>

                    {watermarkType === "text" && (
                      <div>
                        <Label className="text-sm">Rotation: {rotation[0]}°</Label>
                        <Slider
                          value={rotation}
                          onValueChange={setRotation}
                          min={-90}
                          max={90}
                          step={5}
                          className="mt-2"
                        />
                      </div>
                    )}

                    <div>
                      <Label className="text-sm">Position</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {positions.map((pos) => (
                          <Button
                            key={pos.value}
                            variant={position === pos.value ? "default" : "outline"}
                            onClick={() => setPosition(pos.value)}
                            className={position === pos.value ? "bg-cyan-600" : ""}
                            size="sm"
                          >
                            {pos.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Button 
                  onClick={addWatermark}
                  disabled={isProcessing}
                  size="lg"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5 mr-2" />
                  )}
                  Add Watermark & Download
                </Button>
              </div>
            </div>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default WatermarkPdf;
